import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Palette, Wand2, ExternalLink, Download, RefreshCw, Sparkles,
  Loader2, FileText, Film, Image as ImageIcon,
} from "lucide-react";

const KINDS = [
  { v: "flyer_a4",    l: "A4 Flyer (Hochkant)" },
  { v: "flyer_a5",    l: "A5 Flyer" },
  { v: "poster_a3",   l: "A3 Poster" },
  { v: "flyer_dl",    l: "DIN-Lang Flyer" },
  { v: "door_hanger", l: "Türhänger" },
];

const TONES = [
  { v: "eindrucksvoll, filmisch, kurz", l: "Filmisch · dramatisch" },
  { v: "warm, familiär, einladend",     l: "Familiär · warm" },
  { v: "elegant, luxuriös, klassisch",  l: "Elegant · edel" },
  { v: "verspielt, jung, hip",          l: "Jung · verspielt" },
];

export default function Flyers() {
  const [showtimes, setShowtimes] = useState([]);
  const [movies, setMovies] = useState([]);
  const [kind, setKind] = useState("flyer_a4");
  const [showtimeId, setShowtimeId] = useState("");
  const [movieId, setMovieId] = useState("");
  const [tone, setTone] = useState(TONES[0].v);
  const [copy, setCopy] = useState("");
  const [aiPoster, setAiPoster] = useState(false);
  const [accent, setAccent] = useState("#D4AF37");
  const [busy, setBusy] = useState(false);
  const [aiLoad, setAiLoad] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/showtimes").then((r) => setShowtimes(r.data || [])).catch(() => {});
    api.get("/movies").then((r) => setMovies(r.data || [])).catch(() => {});
    api.get("/admin/flyers").then((r) => setHistory(r.data || [])).catch(() => {});
  }, []);

  const buildBody = () => ({
    kind, showtime_id: showtimeId || null, movie_id: movieId || null,
    copy_text: copy, use_ai_poster: aiPoster, accent_hex: accent, tone,
  });

  const aiCopy = async () => {
    setAiLoad(true);
    try {
      const { data } = await api.post("/admin/flyers/ai-copy", buildBody());
      setCopy(data.copy_text || "");
      toast.success("KI-Copy erzeugt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "KI-Copy fehlgeschlagen"); }
    finally { setAiLoad(false); }
  };

  const render = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/flyers/generate", buildBody(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPdfUrl(url);
      toast.success("Flyer erzeugt · Vorschau rechts");
      api.get("/admin/flyers").then((r) => setHistory(r.data || [])).catch(() => {});
    } catch (e) {
      const detail = e.response?.data?.detail || "PDF-Rendering fehlgeschlagen";
      toast.error(typeof detail === "string" ? detail : "Fehler");
    } finally { setBusy(false); }
  };

  const openCanva = async () => {
    try {
      const { data } = await api.get(`/admin/flyers/canva-link?kind=${kind}&showtime_id=${showtimeId || ""}&movie_id=${movieId || ""}`);
      window.open(data.url, "_blank");
    } catch (e) { toast.error("Canva-Link fehlgeschlagen"); }
  };

  return (
    <div className="space-y-6" data-testid="flyers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Marketing</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><Palette className="text-[#D4AF37]" /> Flyer & Prospekte</h1>
          <div className="text-[10px] text-[#575148] mt-1">
            Drei Wege: Canva öffnen · KI-Auto-Fill · Interner Designer mit QR-Buchung
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* Steuerpult */}
        <div className="space-y-4" data-testid="flyers-form">
          <div className="card-lux p-5 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Format</label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {KINDS.map((k) => (
                  <button key={k.v} onClick={() => setKind(k.v)} data-testid={`flyer-kind-${k.v}`}
                    className={`text-[10px] px-2 py-2 uppercase tracking-widest border rounded ${kind === k.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/40"}`}>
                    {k.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorstellung (optional)</label>
                <select value={showtimeId} onChange={(e) => setShowtimeId(e.target.value)}
                  data-testid="flyer-showtime"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none">
                  <option value="">— nur Film wählen —</option>
                  {showtimes.map((s) => (
                    <option key={s.id} value={s.id}>{s.date} · {s.start_time} · {s.movie?.title_de || s.movie_title || "—"}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Film (alternativ)</label>
                <select value={movieId} onChange={(e) => setMovieId(e.target.value)}
                  data-testid="flyer-movie"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none">
                  <option value="">— egal —</option>
                  {movies.map((m) => <option key={m.id} value={m.id}>{m.title_de || m.title_original}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Ton der Ansprache</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)}
                data-testid="flyer-tone"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none">
                {TONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Copy-Text</label>
                <button onClick={aiCopy} disabled={aiLoad} data-testid="flyer-ai-copy"
                  className="text-[10px] uppercase tracking-widest border border-[#8FBC8F]/50 text-[#8FBC8F] px-2 py-1 rounded hover:bg-[#8FBC8F]/10 inline-flex items-center gap-1 disabled:opacity-40">
                  {aiLoad ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} KI-Auto-Text
                </button>
              </div>
              <textarea value={copy} onChange={(e) => setCopy(e.target.value)} rows={8}
                data-testid="flyer-copy"
                placeholder="Leer lassen für KI-Auto-Text — oder eigenen Text eingeben."
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm font-mono focus:border-[#D4AF37] outline-none" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={aiPoster} onChange={(e) => setAiPoster(e.target.checked)}
                  data-testid="flyer-ai-poster" className="mt-1 w-4 h-4 accent-[#D4AF37]" />
                <div>
                  <div className="text-xs text-[#F5F0E6]">KI-Poster (Nano Banana)</div>
                  <div className="text-[10px] text-[#575148]">Sonst wird das TMDb-Poster verwendet</div>
                </div>
              </label>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Akzent-Farbe</label>
                <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)}
                  data-testid="flyer-accent"
                  className="w-full h-10 mt-1 bg-[#050505] border border-[#2A2A2A] rounded cursor-pointer" />
              </div>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button onClick={render} disabled={busy} data-testid="flyer-render"
              className="flex-1 min-w-[140px] bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-4 py-3 rounded text-[11px] uppercase tracking-widest disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} {busy ? "Erzeuge …" : "PDF erzeugen"}
            </button>
            <button onClick={openCanva} data-testid="flyer-canva"
              className="flex-1 min-w-[140px] border border-[#8AB4F8]/60 text-[#8AB4F8] px-4 py-3 rounded text-[11px] uppercase tracking-widest hover:bg-[#8AB4F8]/10 inline-flex items-center justify-center gap-2">
              <ExternalLink size={13} /> In Canva öffnen
            </button>
          </div>

          <div className="text-[10px] text-[#575148] italic">
            <b>A</b> Canva-Deep-Link (Design-Freiheit) · <b>B</b> interner PDF-Renderer mit QR-Buchung · <b>C</b> KI-Auto-Fill (Gemini · Nano Banana)
          </div>
        </div>

        {/* Vorschau */}
        <div className="card-lux p-4 min-h-[600px] flex flex-col" data-testid="flyer-preview">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2">Vorschau</div>
          {!pdfUrl ? (
            <div className="flex-1 flex items-center justify-center text-[#575148] text-sm">
              <div className="text-center">
                <Sparkles size={30} className="mx-auto opacity-60 mb-2" />
                Wähle Format + Vorstellung, dann „PDF erzeugen"
              </div>
            </div>
          ) : (
            <>
              <iframe src={pdfUrl} className="flex-1 w-full rounded border border-[#1A1A1A] bg-white" title="Flyer-Vorschau" />
              <a href={pdfUrl} download="flyer.pdf" data-testid="flyer-download"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#EEDC82] text-black px-3 py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#F5E9A3]">
                <Download size={12} /> Download
              </a>
            </>
          )}
        </div>
      </div>

      {/* Historie */}
      {history.length > 0 && (
        <div className="card-lux p-4">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2">
            <ImageIcon size={12} /> Zuletzt erzeugte Flyer
          </div>
          <div className="grid md:grid-cols-3 gap-2 text-xs">
            {history.slice(0, 12).map((h) => (
              <div key={h.id} className="border border-[#1A1A1A] rounded p-2" data-testid={`flyer-history-${h.id}`}>
                <div className="text-[#EEDC82] truncate">{h.movie || "—"}</div>
                <div className="text-[10px] text-[#575148] font-mono">{(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="text-[10px] text-[#A8A398] mt-1">{h.kind} · {h.by_name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
